import { ExpandLess } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Popper,
  Stack,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { StyledInput } from '../../../UIComponents/StyledInput';
import { SourceReference } from '../../../common/SourceReference';
import { FormError } from '../../../types/FormError';
import { LoadState } from '../../../types/LoadState';
import { formatPersOrgNr } from '../../lib/formatPersnr';
import { PrintButton } from '../PrintButton/PrintButton';
import styles from './ForetagAdditionalInfoRow.module.css';

interface ForetagAdditionalInfoRowProps {
  heading: string;
  loadState: LoadState;
  error: FormError;
  buyDisabled: boolean;
  organisationsnummer: string;
  companyName: string;
  kundmarke: string;
  setKundmarke: (value: string) => void;
  sourceName: string;
}

export const ForetagAdditionalInfoRow: React.FC<
  ForetagAdditionalInfoRowProps
> = ({
  heading,
  loadState,
  error,
  children,
  buyDisabled,
  organisationsnummer,
  companyName,
  kundmarke,
  setKundmarke,
  sourceName,
}) => {
  const printWrapper = useRef<HTMLDivElement>();

  useEffect(() => {
    setIsExpanded(
      loadState === LoadState.Success || loadState === LoadState.Error
    );
  }, [loadState]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isBought = loadState === LoadState.Success;

  return (
    <>
      <Stack
        direction="row"
        className={styles.additionalInfoRow}
        sx={{ cursor: isBought ? 'pointer' : 'default' }}
        onClick={() => isBought && setIsExpanded(!isExpanded)}
      >
        <h2>{heading}</h2>
        <Box
          alignItems="flex-end"
          display="flex"
          justifyContent="flex-end"
          sx={{ flex: '1' }}
        >
          <>
            {!isBought && (
              <>
                <StyledInput
                  label="Kundmärke"
                  value={kundmarke}
                  onChange={(e) => setKundmarke(e.currentTarget.value)}
                  sx={{ mr: 2, background: '#fff' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={buyDisabled || loadState !== LoadState.Idle}
                >
                  {loadState === LoadState.Loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <div
                      style={{ pointerEvents: 'all' }}
                      onMouseEnter={(e) => {
                        setAnchorEl(e.currentTarget);
                      }}
                      onMouseLeave={() => setAnchorEl(null)}
                    >
                      Köp
                      {buyDisabled && (
                        <Popper open={!!anchorEl} anchorEl={anchorEl}>
                          <Alert severity="info">
                            Ditt avtal saknar den transaktionen som krävs för
                            detta köp.
                          </Alert>
                        </Popper>
                      )}
                    </div>
                  )}
                </Button>
              </>
            )}

            <div className={styles.expandArrow}>
              <ExpandLess
                className={`${styles.expander} ${
                  isExpanded ? '' : styles.collapsed
                } ${isBought ? '' : styles.disabled}`}
              />
            </div>
          </>
        </Box>
      </Stack>
      <Collapse in={isExpanded} timeout="auto">
        <div className={styles.content}>
          {error && <Alert severity={error.severity}>{error.text}</Alert>}
          <div ref={printWrapper}>
            <div className={styles.subtileHeader}>
              {companyName}, {formatPersOrgNr(organisationsnummer)}
            </div>
            {children}
            <SourceReference
              sourceName={sourceName}
              style={{ marginTop: '16px' }}
            />
          </div>
          <PrintButton printContents={printWrapper} />
        </div>
      </Collapse>
    </>
  );
};
